/**
 * Advanced Settings Section
 * 
 * Configuration for advanced mapping settings.
 * Updated with unified design tokens.
 */

import React from 'react';
import {
  Box,
  TableBody,
  TableCell,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BodyText, CaptionText } from '../../../../shared/ui/typography';
import {
  CompactStatusSummary,
  InlineHelpIcon,
  MappingEditorHeader,
  MappingRow,
  ScopeEditorTableVariant,
} from '../../../../shared/ui/mapping/ScopeEditorPrimitives';
import { WorkspacePanel } from '../../../../shared/ui/workflow';
import { advancedSettingsTooltips } from '../../../../shared/config/mappingHelpCatalog';
import {
  borderRadius, componentSizes, getControlBorderColor, getSemanticSurfaceTokens,
} from '../../../../theme/theme';
interface AdvancedSettingsSectionProps {
  advancedSettings: {
    centerOfCoordinates: 'ProjectBasePoint' | 'SurveyPoint';
  };
  onCenterOfCoordinatesChange: (value: 'ProjectBasePoint' | 'SurveyPoint') => void;
}

const COLUMN_WIDTHS = {
  setting: '34%',
  configuration: '48%',
  state: '18%',
};

const AdvancedSettingsSection: React.FC<AdvancedSettingsSectionProps> = ({
  advancedSettings,
  onCenterOfCoordinatesChange,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);

  return (
    <WorkspacePanel
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        overflow: 'hidden',
        // Full-bleed surface inside the workspace: the panel border would read
        // as a stray vertical line next to the navigator rail.
        border: 'none',
        // Keep the table header and row text off the pane borders; the shared
        // scope-editor table cells only carry 2px of horizontal padding.
        px: 2,
      }}
    >
      <ScopeEditorTableVariant ariaLabel="PCF export coordinate settings" minWidth={520}>
        <MappingEditorHeader
          columns={[
            { key: 'setting', width: COLUMN_WIDTHS.setting, content: 'Advanced settings' },
            { key: 'configuration', width: COLUMN_WIDTHS.configuration, content: 'Revit source' },
            { key: 'state', width: COLUMN_WIDTHS.state, align: 'center', content: 'Downstream effect' },
          ]}
        />
        <TableBody>
          <MappingRow>
            <TableCell sx={{ width: COLUMN_WIDTHS.setting }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25, minWidth: 0 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <BodyText secondary>
                    Coordinate origin
                  </BodyText>
                  <CaptionText
                    sx={{
                      display: 'block',
                      mt: 0.25,
                      color: colors.text.secondary,
                    }}
                  >
                    Choose the Revit origin subtracted from every exported PCF coordinate: the project base point for project-local coordinates or the survey point for survey-point-relative coordinates. This translation does not rotate coordinates to True North.
                  </CaptionText>
                </Box>
                <InlineHelpIcon
                  label="Coordinate origin"
                  help={advancedSettingsTooltips.centerOfCoordinates}
                />
              </Box>
            </TableCell>
            <TableCell sx={{ width: COLUMN_WIDTHS.configuration }}>
              <ToggleButtonGroup
                exclusive
                value={advancedSettings.centerOfCoordinates}
                onChange={(_event, value) => {
                  if (value) {
                    onCenterOfCoordinatesChange(value);
                  }
                }}
      aria-label="Revit coordinate origin for PCF export"
                size="small"
                sx={{
                  minHeight: componentSizes.buttonSm,
                  borderRadius: `${borderRadius.full}px`,
                  backgroundColor: colors.background.panel,
                  '& .MuiToggleButton-root': {
                    minHeight: componentSizes.buttonSm,
                    px: 0.5,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: colors.text.secondary,
                    borderColor: getControlBorderColor(theme),
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.light,
                      borderColor: getControlBorderColor(theme),
                    },
                  },
                }}
              >
                <ToggleButton value="ProjectBasePoint">Project base point</ToggleButton>
                <ToggleButton value="SurveyPoint">Survey point</ToggleButton>
              </ToggleButtonGroup>
            </TableCell>
            <TableCell sx={{ width: COLUMN_WIDTHS.state, textAlign: 'center' }}>
              <CompactStatusSummary tone="info">
                {advancedSettings.centerOfCoordinates === 'ProjectBasePoint'
                  ? 'Project coordinates'
                  : 'Survey-point relative'}
              </CompactStatusSummary>
            </TableCell>
          </MappingRow>
        </TableBody>
      </ScopeEditorTableVariant>
    </WorkspacePanel>
  );
};

export default AdvancedSettingsSection;
